// src/pages/CreateTestPage.jsx
import React, { useMemo, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  useGetTextbooksQuery,
  useGetTextbookByIdQuery,
} from "../context/textbookApi";
import { useGenerateAiTestMutation } from "../context/apiSlice";
import { useGenerateFileTestMutation } from "../context/fileTestApi";


/* --- til ro'yxati --- */
const LANGS = [
  { code: "uz", label: "O‘zbek" },
  { code: "ru", label: "Русский" },
  { code: "en", label: "English" },
];

export default function CreateTestPage() {
  /* --- REJIM: "ai" | "file" --- */
  const [mode, setMode] = useState("ai");

  /* === BACKENDDAN DARS LIKLAR RO‘YXATI === */
  const {
    data: textbooks = [],
    isLoading: isTextbooksLoading,
    error: textbooksError,
  } = useGetTextbooksQuery();

  /* --- AI REJIMI STATE --- */
  const [grade, setGrade] = useState(""); // masalan: "5"
  const [subject, setSubject] = useState(""); // masalan: "Algebra"
  const [selectedTopicId, setSelectedTopicId] = useState(""); // textbook_topics.id
  const [questionCount, setQuestionCount] = useState(15);
  const [variantCount, setVariantCount] = useState(4);
  const [testLang, setTestLang] = useState("uz");
  const [difficulty, setDifficulty] = useState("oson");

  const [generateAiTest, { isLoading: isGenerating, error: generateError }] =
    useGenerateAiTestMutation();
    const [generatedTest, setGeneratedTest] = useState(null);
    const [
      generateFileTest,
      { isLoading: isFileGenerating, error: fileError },
    ] = useGenerateFileTestMutation();
    

  /* --- FAYL REJIMI STATE (hozircha demo) --- */
  const [uploadFile, setUploadFile] = useState(null);
  const [lessonName, setLessonName] = useState("");
  const [fileQuestionCount, setFileQuestionCount] = useState(15);
  const [fileVariantCount, setFileVariantCount] = useState(4);
  const [fileTestLang, setFileTestLang] = useState("uz");
  const [fileDifficulty, setFileDifficulty] = useState("onson");
  const [status, setStatus] = useState("idle");

  /* ---------- DARS LIKLARDAN SINF / FAN OPsiyalar ---------- */

  // Unique sinflar: ["2", "5", "11", ...]
  const gradeOptions = useMemo(() => {
    if (!textbooks) return [];
    const set = new Set(
      textbooks
        .filter((t) => t.grade !== null && t.grade !== undefined)
        .map((t) => String(t.grade))
    );
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  }, [textbooks]);

  // Tanlangan sinf bo‘yicha fanlar
  const subjectOptions = useMemo(() => {
    if (!textbooks || !grade) return [];
    const set = new Set(
      textbooks
        .filter((t) => String(t.grade) === String(grade))
        .map((t) => t.subject)
        .filter(Boolean)
    );
    return Array.from(set);
  }, [textbooks, grade]);

  // Boshlang‘ich grade/subject ni avtomatik qo‘yish
  useEffect(() => {
    if (!grade && gradeOptions.length) {
      setGrade(gradeOptions[0]);
    }
  }, [grade, gradeOptions]);

  useEffect(() => {
    if (!subject && subjectOptions.length) {
      setSubject(subjectOptions[0]);
    }
  }, [subject, subjectOptions]);

  /* ---------- TANLANGAN SINF+FAN UCHUN AKTIV DARSLIK ---------- */

  // Shu sinf va fan bo‘yicha darsliklar (bir nechta bo‘lishi mumkin)
  const activeTextbookId = useMemo(() => {
    if (!textbooks || !grade || !subject) return null;

    const candidates = textbooks.filter(
      (t) =>
        String(t.grade) === String(grade) &&
        t.subject === subject
    );

    if (!candidates.length) return null;

    // eng oxirgi qo‘shilganini olamiz
    candidates.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at)
    );
    return candidates[0].id;
  }, [textbooks, grade, subject]);

  // Tanlangan darslikning detali + mavzulari
  const {
    data: textbookDetail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useGetTextbookByIdQuery(activeTextbookId, {
    skip: !activeTextbookId,
  });

  const topicsRaw = textbookDetail?.topics || [];

  // Mavzular ro‘yxatini UI uchun tayyorlash
  const topicsForCurrent = useMemo(
    () =>
      topicsRaw.map((t, idx) => ({
        id: t.id,
        label:
          t.title || t.topic_title || `Mavzu ${idx + 1}`,
      })),
    [topicsRaw]
  );

  /* ---------- HANDLERS (AI REJIM) ---------- */

  const handleCreateTestAI = async (e) => {
    e.preventDefault();
    setGeneratedTest(null);

    if (!activeTextbookId) {
      alert("Bu sinf va fan uchun darslik topilmadi. Avval darslik yarating.");
      return;
    }
    if (!selectedTopicId) {
      alert("Iltimos, mavzuni tanlang.");
      return;
    }

    try {
      const res = await generateAiTest({
        textbookId: activeTextbookId,
        topicId: selectedTopicId,
        questionCount: Number(questionCount),
        optionCount: Number(variantCount),
        difficulty,
        language: testLang,
      }).unwrap();

      setGeneratedTest(res);
    } catch (err) {
      console.error("AI test generatsiya xatosi:", err);
    }
  };

  /* ---------- HANDLERS (FAYL REJIM) ---------- */

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    setUploadFile(file || null);
    if (file && !lessonName) {
      const rawName = file.name.replace(/\.[^/.]+$/, "");
      setLessonName(rawName);
    }
  };

  const handleCreateTestFromFile = async (e) => {
    e.preventDefault();
    if (!uploadFile) return;
  
    setStatus("loading");
  
    try {
      const res = await generateFileTest({
        file: uploadFile,
        questionCount: Number(fileQuestionCount),
        optionCount: Number(fileVariantCount),
        language: fileTestLang,
        difficulty: fileDifficulty,
      }).unwrap();
  
      setStatus("idle");
  
      // backend { success, testId }
      alert(`Test yaratildi! ID: ${res.testId}`);
  
      // xohlasang redirect
      // navigate(`/tests/${res.testId}`);
    } catch (err) {
      console.error("Fayldan test yaratish xatosi:", err);
      setStatus("idle");
    }
  };
  

  const toggleMode = () => {
    setMode((prev) => (prev === "ai" ? "file" : "ai"));
    setGeneratedTest(null);
    setStatus("idle");
  };

  /* ---------- UI ---------- */
  const total = generatedTest?.questions?.length ?? generatedTest?.length ?? 0;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4 space-y-8">
        {/* Title + mode button */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Test yaratish
            </h1>
            <Link
              to="/admin/textbooks/new"
              className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border border-gray-200 bg-white hover:bg-gray-100 mt-2 box-decoration-clone bg-clip-text text-transparent bg-gradient-to-r from-indigo-500 to-fuchsia-500"
            >
              Kitob va Mavzu yaratish
            </Link>
            <p className="mt-2 text-sm text-gray-500">
              Sinf, fan va mavzuni tanlang, so‘ng AI yoki fayl orqali testni
              generatsiya qiling.
            </p>
          </div>

          <button
            type="button"
            onClick={toggleMode}
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium border border-gray-200 bg-white hover:bg-gray-100"
          >
            {mode === "ai" ? "Fayl yuklash" : "Sinf bo‘yicha yaratish"}
          </button>
        </div>

        {/* AI rejimi uchun yuklanish/xato holatlari */}
        {mode === "ai" && isTextbooksLoading && (
          <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 text-sm text-gray-500">
            Darsliklar yuklanmoqda...
          </div>
        )}

        {mode === "ai" && textbooksError && (
          <div className="bg-white rounded-3xl shadow-sm border border-red-200 p-6 text-sm text-red-600">
            Darsliklarni yuklashda xato:
            {" "}{textbooksError?.data?.message || ""}
          </div>
        )}

        {/* ---------- AI REJIMI ---------- */}
        {mode === "ai" && !isTextbooksLoading && !textbooksError && (
          <>
            <form
              onSubmit={handleCreateTestAI}
              className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8 space-y-6"
            >
              <h2 className="text-sm font-semibold text-gray-700 mb-2">
                Test sozlamalari
              </h2>

              {/* sinf + fan */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* sinf */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Sinf
                  </label>
                  <select
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                    value={grade}
                    onChange={(e) => {
                      setGrade(e.target.value);
                      setSubject("");
                      setSelectedTopicId("");
                    }}
                  >
                    {gradeOptions.length === 0 && (
                      <option value="">Darsliklar yo‘q</option>
                    )}
                    {gradeOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                {/* fan */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Fan
                  </label>
                  <select
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                    value={subject}
                    onChange={(e) => {
                      setSubject(e.target.value);
                      setSelectedTopicId("");
                    }}
                    disabled={!subjectOptions.length}
                  >
                    {!subjectOptions.length && (
                      <option value="">
                        Tanlangan sinf uchun fan topilmadi
                      </option>
                    )}
                    {subjectOptions.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Mavzu dropdown */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Mavzuni tanlang
                </label>

                {!activeTextbookId ? (
                  <p className="text-xs text-gray-400 italic">
                    Bu sinf va fan uchun darslik hali qo‘shilmagan.
                  </p>
                ) : isDetailLoading ? (
                  <p className="text-xs text-gray-400 italic">
                    Mavzular yuklanmoqda...
                  </p>
                ) : detailError ? (
                  <p className="text-xs text-red-500">
                    Mavzularni yuklashda xato.
                  </p>
                ) : topicsForCurrent.length === 0 ? (
                  <p className="text-xs text-gray-400 italic">
                    Bu darslik uchun mavzular hali qo‘shilmagan.
                  </p>
                ) : (
                  <select
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                    value={selectedTopicId}
                    onChange={(e) => setSelectedTopicId(e.target.value)}
                  >
                    <option value="">Mavzuni tanlang</option>
                    {topicsForCurrent.map((topic) => (
                      <option key={topic.id} value={topic.id}>
                        {topic.label}
                      </option>
                    ))}
                  </select>
                )}

                <p className="mt-1 text-[11px] text-gray-400">
                  Tanlangan sinf va fanga mos mavzular ro‘yxati.
                </p>
              </div>

              {/* Savollar soni / variantlar soni */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Savollar soni
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={questionCount}
                    onChange={(e) => setQuestionCount(e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    1–50 ta savol kiritish tavsiya etiladi.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Savol uchun variantlar soni
                  </label>
                  <select
                    value={variantCount}
                    onChange={(e) =>
                      setVariantCount(Number(e.target.value))
                    }
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    <option value={3}>3 ta variant</option>
                    <option value={4}>4 ta variant</option>
                    <option value={5}>5 ta variant</option>
                  </select>
                </div>
              </div>

              {/* daraja / til */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Test darajasi
                  </label>
                  <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    <option value="oson">onson</option>
                    <option value="orta">o‘rta</option>
                    <option value="qiyin">qiyin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Test tili
                  </label>
                  <select
                    value={testLang}
                    onChange={(e) => setTestLang(e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    {LANGS.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Yaratish tugmasi */}
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gray-700 text-white text-sm py-3.5 hover:bg-gray-800 transition disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={
                    !activeTextbookId ||
                    !topicsForCurrent.length ||
                    !selectedTopicId ||
                    isGenerating
                  }
                >
                  {isGenerating
                    ? "Test generatsiya qilinmoqda..."
                    : "Test yaratish"}
                </button>
                <p className="mt-2 text-[11px] text-gray-400 text-center">
                  Tanlangan parametrlar asosida test generatsiya qilinadi.
                </p>
              </div>

              {generateError && (
                <div className="mt-3 text-xs text-red-600">
                  Xato:{" "}
                  {generateError.data?.message ||
                    generateError.error ||
                    "AI test generatsiya xatosi"}
                </div>
              )}
            </form>

            {/* Natija bloki (qisqacha preview) */}
            {!generatedTest && !isGenerating && (
              <div className="text-center text-xs text-gray-400 mt-4">
                Test hali yaratilmagan. Yuqoridagi formani to‘ldirib “Test
                yaratish” tugmasini bosing.
              </div>
            )}

            {generatedTest && (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 mt-4">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">
                  Test yaratildi ✔️
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Jami savollar:{" "}
                  <span className="font-semibold">
                  {total}

                  </span>
                </p>

                {generatedTest.questions?.map((q, i) => (
                  <div key={i} className="mb-3">
                    <p className="text-xs font-medium text-gray-800">
                      {i + 1}. {q.question}
                    </p>

                    <ul className="mt-1 text-[11px] text-gray-600 space-y-1">
                      {q.options?.map((opt, idx) => (
                        <li key={idx}>
                          {String.fromCharCode(65 + idx)}. {opt}
                          {idx === q.answerIndex && (
                            <span className="text-emerald-600 font-semibold ml-1">
                              (to‘g‘ri)
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}


                {generatedTest.questions?.length > 3 && (
                  <p className="text-[11px] text-gray-400">
                    Qolgan savollarni keyingi sahifada ko‘rsatish/saqlash
                    logikasini qo‘shish mumkin.
                  </p>
                )}
              </div>
            )}
          </>
        )}

        {/* ---------- FAYL REJIMI ---------- */}
        {mode === "file" && (
          <form onSubmit={handleCreateTestFromFile} className="space-y-6">
            {/* FAYL YUKLASH */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-4">
              <h2 className="text-xs font-semibold text-gray-500 tracking-wide">
                FAYL YUKLASH
              </h2>

              <label className="block">
                <div className="w-full border border-dashed border-gray-300 rounded-2xl px-4 py-6 text-center cursor-pointer hover:bg-gray-50">
                  {uploadFile ? (
                    <div className="flex items-center justify-between text-sm text-gray-700">
                      <div className="flex flex-col items-start">
                        <span className="font-medium">
                          {uploadFile.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {(uploadFile.size / 1024 / 1024).toFixed(2)} MB
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadFile(null);
                          setLessonName("");
                        }}
                        className="text-xs text-gray-500 hover:text-red-500"
                      >
                        ✕
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500">
                      PDF yoki boshqa hujjatni bu yerga yuklang
                    </div>
                  )}
                </div>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.txt"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </label>
            </div>

            {/* TEST SOZLAMALARI */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8 space-y-5">
              <h2 className="text-xs font-semibold text-gray-500 tracking-wide">
                TEST SOZLAMALARI
              </h2>

              {/* Dars nomi */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Dars nomi
                </label>
                <input
                  type="text"
                  value={lessonName}
                  onChange={(e) => setLessonName(e.target.value)}
                  placeholder="Masalan: React, Node.js, Tarix 5-sinf..."
                  className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                />
              </div>

              {/* Savollar soni / variantlar */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Savollar soni
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    value={fileQuestionCount}
                    onChange={(e) => setFileQuestionCount(e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  />
                  <p className="mt-1 text-[11px] text-gray-400">
                    1–50 ta savol kiriting.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Savol uchun variantlar
                  </label>
                  <select
                    value={fileVariantCount}
                    onChange={(e) =>
                      setFileVariantCount(Number(e.target.value))
                    }
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    <option value={3}>3</option>
                    <option value={4}>4</option>
                    <option value={5}>5</option>
                  </select>
                </div>
              </div>

              {/* daraja / til */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Test darajasi
                  </label>
                  <select
                    value={fileDifficulty}
                    onChange={(e) => setFileDifficulty(e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    <option value="onson">onson</option>
                    <option value="orta">o‘rta</option>
                    <option value="qiyin">qiyin</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Test tili
                  </label>
                  <select
                    value={fileTestLang}
                    onChange={(e) => setFileTestLang(e.target.value)}
                    className="w-full rounded-2xl border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
                  >
                    {LANGS.map((l) => (
                      <option key={l.code} value={l.code}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Yaratish tugmasi */}
              <div className="pt-2">
                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gray-900 text-white text-sm py-3.5 hover:bg-black transition disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={!uploadFile || status === "loading"}
                >
                  {status === "loading"
                    ? "Test generatsiya qilinmoqda..."
                    : "▶ Test yaratish"}
                </button>
              </div>
            </div>

            {status === "idle" && (
              <div className="text-center text-xs text-gray-400">
                Test hali yaratilmagan. Fayl yuklab, sozlamalarni kiriting va
                “Test yaratish” tugmasini bosing.
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
